import { Formik } from 'formik';
import { useAlert,
  AsyncButton, CodeInput, useInitiateContactVerification, useVerifyContact
} from '@uplift/ui-v2';

import { Box, FormHelperText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ContactName, ContactType } from '@uplift/types';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useUserContactsQuery } from '@uplift/queries';

import {
  StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components';
import { VerifyPhoneCodeNextData } from '../types';

export type VerifyPhoneCodeProps = {
  onBack: () => void;
  onNext: (data: VerifyPhoneCodeNextData) => void;
};

type VerifyPhoneCodeFormType = {
  code: string;
};

export const VerifyPhoneCode: React.VFC<VerifyPhoneCodeProps> = props => {
  const l10n = useL10n();
  const currentUser = useCurrentuser();
  const { classes } = useVerifyPhoneCodeStyles();
  const alert = useAlert();
  const { initiateContactVerification } = useInitiateContactVerification();
  const { data: contacts = [] } = useUserContactsQuery(currentUser.id);
  const mobile = contacts.find(c => c.type === ContactType.phone && c.name === ContactName.mobile);
  const { verifyContact, error: verifyContactError } = useVerifyContact();

  const helperErrorMessage = verifyContactError?.error
    ? l10n.phoneValidationModal.errors[verifyContactError.error]
    : undefined;

  const onResendCode = async () => {
    if (!mobile) {
      return;
    }

    await initiateContactVerification({ contactId: mobile.id });

    alert.success(<strong>{l10n.phoneValidationModal.newCodeSent}</strong>);
  };

  const onSubmit = async (values: VerifyPhoneCodeFormType) => {
    if (!mobile) {
      return;
    }

    await verifyContact({ verificationCode: values.code, contactId: mobile.id });
    alert.success(<strong>Phone number verified</strong>);
    props.onNext({ phoneVerified: true });
  };

  return (
    <Formik
      initialValues={{
        code: '',
      }}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <StepContainer>
          <StepContent className={classes.content}>
            <Box>
              <StepHeading>Enter verification code</StepHeading>
              <StepDescription className={classes.description}>
                A text message with a verification code has been sent to {mobile?.value}.
              </StepDescription>
              <StepForm onSubmit={formik.handleSubmit} maxWidth="384px">
                <CodeInput name="code" codeLength={4} onSubmit={formik.handleSubmit} autoFocus data-testid="code-input" />
                <FormHelperText error={!!helperErrorMessage} className={classes.helperText}>
                  {helperErrorMessage || l10n.phoneValidationModal.yourCodeWillExpire}
                </FormHelperText>
                <AsyncButton
                  color="secondary"
                  className={classes.resendButton}
                  onClick={onResendCode}
                  data-testid="resend-code-btn"
                >
                  Resend Code
                </AsyncButton>
              </StepForm>
            </Box>
            <Box className={classes.termsContainer}>
              <Typography variant="caption" color="textSecondary">
                By verifying your phone number, you agree to receive text message notifications from UpLift. To opt out,
                text STOP at any time. Message frequency varies, and standard carrier rates may apply. For more details
                visit our <a href="https://www.joinuplift.co/terms-of-use" target="_blank" rel="noreferrer">Terms of Service</a>.
              </Typography>
            </Box>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton
              disabled={formik.values.code.length !== 4}
              loading={formik.isSubmitting}
              data-testid="verify-btn"
            >
              verify
            </StepNextButton>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const useVerifyPhoneCodeStyles = makeStyles({ name: 'VerifyPhoneCode' })((theme) => ({
  description: {
    marginTop: theme.spacing(3),
  },
  helperText: {
    marginTop: theme.spacing(2),
  },
  resendButton: {
    textTransform: 'none',
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(-2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  termsContainer: {
    margin: theme.spacing(2),
    '& a': {
      color: 'inherit'
    },
  },
}));
